import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader, BottomNav } from "@/components/app-header";

// Static Maps image — $2/1000 vs $7/1000 for the full JS SDK
const STATIC_MAP_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
const ACCRA_STATIC_MAP = STATIC_MAP_KEY
  ? `https://maps.googleapis.com/maps/api/staticmap?center=5.6037,-0.187&zoom=13&size=800x400&scale=2&style=feature:poi|visibility:off&style=feature:transit|visibility:off&key=${STATIC_MAP_KEY}`
  : "";
import {
  MapPin,
  Search,
  Clock,
  Star,
  ArrowRight,
  Sparkles,
  Car,
} from "lucide-react";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";
import { useRecentPlacesStore } from "@/store/recentPlacesStore";

export default function PassengerHome() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [loading, setLoading] = useState(false);

  const recentPlaces = useRecentPlacesStore((s) => s.places);
  const addRecentPlace = useRecentPlacesStore((s) => s.addPlace);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pickup || !destination) {
      toast.error("Please enter both pickup and destination");
      return;
    }

    setLoading(true);
    try {
      if (pickup) addRecentPlace({ address: pickup });
      if (destination) addRecentPlace({ address: destination });
      navigate(
        `/passenger/drivers?pickup=${encodeURIComponent(pickup)}&destination=${encodeURIComponent(destination)}&rideType=BASIC`,
      );
    } catch (error) {
      toast.error("Error searching for drivers");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-20 md:pb-0">
      <AppHeader />

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Hello, {user?.full_name?.split(" ")[0] || "there"} 👋
          </h1>
          <p className="text-gray-500 mt-1">Where are you going today?</p>
        </div>

        {/* Search Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 mb-8">
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Pickup */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 bg-yenko-blue rounded-full" />
              <Input
                type="text"
                placeholder="Pickup location"
                value={pickup}
                onChange={(e) => setPickup(e.target.value)}
                className="h-14 pl-10 rounded-2xl bg-gray-50 border-0 text-base font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-yenko-blue/20"
                required
              />
            </div>

            {/* Connector line */}
            <div className="flex items-center gap-3 px-4">
              <div className="w-0.5 h-6 bg-gray-200 ml-1" />
            </div>

            {/* Destination */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 bg-yenko-yellow rounded-full" />
              <Input
                type="text"
                placeholder="Where to?"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="h-14 pl-10 rounded-2xl bg-gray-50 border-0 text-base font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-yenko-blue/20"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-yenko-blue to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl font-bold text-base shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:scale-[1.02]"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Searching...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Find a Ride
                  <Car className="w-5 h-5" />
                </div>
              )}
            </Button>
          </form>
        </div>

        {/* Recent Places */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Recent Places
            </h2>
            <button className="text-sm font-semibold text-yenko-blue hover:underline">
              See all
            </button>
          </div>
          <div className="space-y-3">
            {recentPlaces.length === 0 ? (
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-dashed border-gray-200 text-gray-400 text-sm">
                <Clock className="w-5 h-5 shrink-0" />
                No recent places yet — your searches will appear here.
              </div>
            ) : (
              recentPlaces.map((place, index) => (
                <button
                  key={index}
                  onClick={() => setDestination(place.address)}
                  className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 hover:border-yenko-blue/30 hover:shadow-md transition-all text-left group"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center group-hover:bg-yenko-blue/10 transition-colors">
                    <Clock className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 font-medium truncate">
                      {place.address}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-yenko-blue group-hover:translate-x-1 transition-all" />
                </button>
              ))
            )}
          </div>
        </div>

        {/* Map Preview Background/Featured Section */}
        <div className="mb-8 group">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              Your Surroundings
            </h2>
            <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full flex items-center gap-1 animate-pulse">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Live
            </span>
          </div>
          <div className="h-64 rounded-3xl overflow-hidden shadow-xl border border-gray-100 relative bg-gray-100">
            {ACCRA_STATIC_MAP ? (
              <img
                src={ACCRA_STATIC_MAP}
                alt="Accra, Ghana map"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
                <p className="text-blue-400 text-sm font-medium">
                  Accra, Ghana
                </p>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/20 to-transparent">
              <p className="text-white text-xs font-medium drop-shadow-md px-3 py-1 bg-black/30 backdrop-blur-md rounded-full w-fit">
                Accra, Ghana
              </p>
            </div>
          </div>
        </div>

        {/* Promo Card */}
        <div className="bg-gradient-to-r from-yenko-blue to-blue-600 rounded-3xl p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-yenko-yellow" />
              <span className="text-sm font-bold text-yenko-yellow">NEW</span>
            </div>
            <h3 className="text-xl font-bold mb-2">Refer a friend</h3>
            <p className="text-white/80 text-sm mb-4">
              Get ₵10 off your next ride when you refer a friend to Yenko
            </p>
            <Button className="bg-white text-yenko-blue hover:bg-gray-100 font-bold rounded-xl h-10 px-6">
              Invite Friends
            </Button>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
